// Hook that, given the active panel filter, returns the list of cards to
// render plus a count, a section header label, and a per-row edit-handler
// factory. Single normalisation point for the five entity types so the
// RTLSPanel JSX stays flat.
//
// Status normalisation:
//   - personnel/equipment use AssetStatus directly (active/idle/alert/offline)
//   - locations always 'ok' (no status enum)
//   - access points map: active→ok, offline→idle, fault→alert
//   - chambers map: available→ok, near_capacity→warn, full→alert, maintenance→idle
//
// Click semantics:
//   - personnel/equipment → dispatch selectAsset(id)
//   - location/AP/chamber → sceneRef.camera.flyTo(pos)

import {useMemo} from 'react'
import {useAppSelector} from '../../app/useAppSelector'
import {useAppDispatch} from '../../app/useAppDispatch'
import {rtlsActions} from '@shared/slices/rtlsSlice'
import {useSceneRef} from '../../scene/SceneRefContext'
import type {EntityCardProps, EntityKind, EntityStatusKey} from './EntityCard'
import type {
  AccessPointStatus,
  AssetStatus,
  ChamberStatus,
  FilterLevel,
} from '@shared/types/rtlsTypes'

/** The panel's filter selector — a superset of EntityKind that adds the
 *  cross-cutting "all" view (mixed flat list of every entity type). */
export type PanelFilter = EntityKind | 'all'

const HEADER: Record<PanelFilter, string> = {
  all:          'All entities',
  personnel:    'Personnel',
  equipment:    'Equipment',
  location:     'Locations',
  access_point: 'Access points',
  chamber:      'Rescue chambers',
}

function formatTimeSince(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 10) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

function normalizeAssetStatus(s: AssetStatus): EntityStatusKey {
  return s // already aligned
}
function normalizeAPStatus(s: AccessPointStatus): EntityStatusKey {
  if (s === 'fault') return 'alert'
  if (s === 'active') return 'ok'
  return 'idle'
}
function normalizeChamberStatus(s: ChamberStatus): EntityStatusKey {
  if (s === 'full') return 'alert'
  if (s === 'near_capacity') return 'warn'
  if (s === 'available') return 'ok'
  return 'idle'
}

// Alphabetical sort by the visible identifier with natural number ordering
// (so "DR3" sorts before "DR302" instead of after it lexicographically).
// Case-insensitive so casing differences don't shuffle adjacent rows.
function byPrimary(a: EntityCardProps, b: EntityCardProps): number {
  return a.primary.localeCompare(b.primary, undefined, {numeric: true, sensitivity: 'base'})
}

// Per-kind aggregate stats — `active` rolls up both raw 'active' and the
// generic 'ok' normalization (APs / chambers), `offline` includes 'idle'
// for the same reason. 'warn' (chamber near_capacity) is omitted from the
// 4-stat grid by design — adding a fifth column wasn't requested.
function aggregateStats(keys: EntityStatusKey[]): UseEntityListResult['stats'] {
  let total = 0, active = 0, alert = 0, offline = 0
  for (const k of keys) {
    total++
    if (k === 'active' || k === 'ok') active++
    else if (k === 'alert') alert++
    else if (k === 'offline' || k === 'idle') offline++
  }
  return {total, active, alert, offline}
}

export interface UseEntityListResult {
  /** Card props ready to spread into <EntityCard {...row} />. */
  entities: EntityCardProps[]
  /** Pre-filter count of entities of the active kind (before status/level filters). */
  totalCount: number
  /** Post-filter count (what's actually rendered). */
  visibleCount: number
  headerLabel: string
  /** Does this filter expose RTLS-style status chips (active/idle/alert/offline)? */
  supportsStatusFilter: boolean
  /** Does this filter expose a "+ Add" button (and which dialog to open)? */
  addsTo: 'location' | 'access_point' | null
  /** Pre-filter aggregate stats for the active kind. Drives the 4-stat
   *  grid in the panel — recomputes whenever the Show filter changes. */
  stats: {
    total: number
    active: number
    alert: number
    offline: number
  }
}

interface Args {
  panelFilter: PanelFilter
  /** When non-null, restrict to entities at this level. */
  levelFilter: FilterLevel
  /** When non-'all' AND filter is personnel/equipment, restrict by AssetStatus. */
  statusFilter: 'all' | AssetStatus
  /** Lookup that returns the right "open edit dialog" handler. Only the
   *  concrete entity kinds reach this — the 'all' filter never edits. */
  onEdit: (kind: EntityKind, id: string) => void
}

export function useEntityListForFilter({
  panelFilter,
  levelFilter,
  statusFilter,
  onEdit,
}: Args): UseEntityListResult {
  const dispatch     = useAppDispatch()
  const sceneRef     = useSceneRef()

  const assets       = useAppSelector(s => s.rtls.assets)
  const locations    = useAppSelector(s => s.rtls.locations)
  const accessPoints = useAppSelector(s => s.rtls.accessPoints)
  const chambers     = useAppSelector(s => s.rtls.rescueChambers)
  const selectedId   = useAppSelector(s => s.rtls.selectedAssetId)

  return useMemo<UseEntityListResult>(() => {
    const headerLabel = HEADER[panelFilter]
    const matchesLevel = (lvl?: number) =>
      levelFilter === 'all' || lvl === levelFilter

    // ── All entities (cross-type flat list) ───────────────────────────
    if (panelFilter === 'all') {
      const all: EntityCardProps[] = []
      const aggregateKeys: EntityStatusKey[] = []

      // Order matches the chip-row priority: people first, then equipment,
      // then infrastructure, then read-only safety.
      for (const a of Object.values(assets)) {
        if (!matchesLevel(a.level)) continue
        const key = normalizeAssetStatus(a.status)
        aggregateKeys.push(key)
        const isEquipment = a.type === 'equipment'
        const primary = isEquipment ? (a.externalId ?? a.name) : a.name
        const metaParts: string[] = []
        if (a.zone) metaParts.push(`Zone ${a.zone}`)
        if (a.level != null) metaParts.push(`Lv ${a.level}`)
        all.push({
          kind: a.type,
          id: a.id,
          primary,
          secondary: a.subType?.replace(/_/g, ' '),
          meta: metaParts.length ? metaParts.join(' · ') : undefined,
          timeAgo: formatTimeSince(a.lastUpdated ?? Date.now()),
          statusKey: key,
          statusLabel: a.status,
          selected: a.id === selectedId,
          onClick: () => dispatch(rtlsActions.selectAsset(a.id)),
          onEdit: () => onEdit(a.type, a.id),
        })
      }
      for (const l of Object.values(locations)) {
        if (!matchesLevel(l.level)) continue
        aggregateKeys.push('ok')
        all.push({
          kind: 'location',
          id: l.id,
          primary: l.name,
          secondary: l.type,
          meta: l.level != null ? `Lv ${l.level}` : undefined,
          statusKey: 'ok',
          onClick: () => {
            sceneRef.current?.camera.flyTo(l.position, 150, 1.2, {x: 0, y: 0, z: 1})
          },
          onEdit: () => onEdit('location', l.id),
        })
      }
      for (const ap of Object.values(accessPoints)) {
        if (!matchesLevel(ap.level)) continue
        const key = normalizeAPStatus(ap.status)
        aggregateKeys.push(key)
        const parent = ap.locationId ? locations[ap.locationId] : undefined
        all.push({
          kind: 'access_point',
          id: ap.id,
          primary: ap.name,
          secondary: parent?.name,
          meta: ap.level != null ? `Lv ${ap.level}` : undefined,
          statusKey: key,
          statusLabel: ap.status,
          onClick: () => {
            sceneRef.current?.camera.flyTo(ap.position, 80, 1.2, {x: 0, y: 0, z: 1})
          },
          onEdit: () => onEdit('access_point', ap.id),
        })
      }
      for (const c of Object.values(chambers)) {
        if (!matchesLevel(c.level)) continue
        const key = normalizeChamberStatus(c.status)
        aggregateKeys.push(key)
        all.push({
          kind: 'chamber',
          id: c.id,
          primary: c.name,
          secondary: c.location,
          meta: `${c.currentOccupancy}/${c.capacity} · ${c.capacity > 0
            ? Math.round((c.currentOccupancy / c.capacity) * 100)
            : 0}% full`,
          statusKey: key,
          statusLabel: c.status.replace(/_/g, ' '),
          onClick: () => {
            sceneRef.current?.camera.flyTo(c.position, 120, 1.2, {x: 0, y: 0, z: 1})
          },
          // No onEdit for chambers — TagBoard owns chamber CRUD.
        })
      }

      all.sort(byPrimary)
      return {
        entities: all,
        totalCount: all.length,
        visibleCount: all.length,
        headerLabel,
        supportsStatusFilter: false,
        addsTo: null,
        stats: aggregateStats(aggregateKeys),
      }
    }

    // ── Personnel / Equipment ─────────────────────────────────────────
    if (panelFilter === 'personnel' || panelFilter === 'equipment') {
      const all = Object.values(assets).filter(a => a.type === panelFilter)
      const totalCount = all.length
      const stats = aggregateStats(all.map(a => normalizeAssetStatus(a.status)))

      const filtered = all
        .filter(a => matchesLevel(a.level))
        .filter(a => statusFilter === 'all' || a.status === statusFilter)

      const entities: EntityCardProps[] = filtered.map(a => {
        const isEquipment = a.type === 'equipment'
        const primary = isEquipment
          ? (a.externalId ?? a.name)
          : a.name
        const metaParts: string[] = []
        if (a.zone) metaParts.push(`Zone ${a.zone}`)
        if (a.level != null) metaParts.push(`Lv ${a.level}`)
        return {
          kind: panelFilter,
          id: a.id,
          primary,
          secondary: a.subType?.replace(/_/g, ' '),
          meta: metaParts.length ? metaParts.join(' · ') : undefined,
          timeAgo: formatTimeSince(a.lastUpdated ?? Date.now()),
          statusKey: normalizeAssetStatus(a.status),
          statusLabel: a.status,
          selected: a.id === selectedId,
          onClick: () => dispatch(rtlsActions.selectAsset(a.id)),
          onEdit: () => onEdit(panelFilter, a.id),
        }
      })
      entities.sort(byPrimary)
      return {
        entities,
        totalCount,
        visibleCount: entities.length,
        headerLabel,
        supportsStatusFilter: true,
        addsTo: null,
        stats,
      }
    }

    // ── Locations ─────────────────────────────────────────────────────
    if (panelFilter === 'location') {
      const all = Object.values(locations)
      const totalCount = all.length
      const stats = aggregateStats(all.map(() => 'ok' as EntityStatusKey))
      const filtered = all
        .filter(l => matchesLevel(l.level))

      const entities: EntityCardProps[] = filtered.map(l => ({
        kind: 'location',
        id: l.id,
        primary: l.name,
        secondary: l.type,
        meta: l.level != null ? `Lv ${l.level}` : undefined,
        statusKey: 'ok',
        onClick: () => {
          sceneRef.current?.camera.flyTo(l.position, 150, 1.2, {x: 0, y: 0, z: 1})
        },
        onEdit: () => onEdit('location', l.id),
      }))
      entities.sort(byPrimary)
      return {
        entities,
        totalCount,
        visibleCount: entities.length,
        headerLabel,
        supportsStatusFilter: false,
        addsTo: 'location',
        stats,
      }
    }

    // ── Access Points ─────────────────────────────────────────────────
    if (panelFilter === 'access_point') {
      const all = Object.values(accessPoints)
      const totalCount = all.length
      const stats = aggregateStats(all.map(ap => normalizeAPStatus(ap.status)))
      const filtered = all
        .filter(ap => matchesLevel(ap.level))

      const entities: EntityCardProps[] = filtered.map(ap => {
        const parent = ap.locationId ? locations[ap.locationId] : undefined
        return {
          kind: 'access_point',
          id: ap.id,
          primary: ap.name,
          secondary: parent?.name,
          meta: ap.level != null ? `Lv ${ap.level}` : undefined,
          statusKey: normalizeAPStatus(ap.status),
          statusLabel: ap.status,
          onClick: () => {
            sceneRef.current?.camera.flyTo(ap.position, 80, 1.2, {x: 0, y: 0, z: 1})
          },
          onEdit: () => onEdit('access_point', ap.id),
        }
      })
      entities.sort(byPrimary)
      return {
        entities,
        totalCount,
        visibleCount: entities.length,
        headerLabel,
        supportsStatusFilter: false,
        addsTo: 'access_point',
        stats,
      }
    }

    // ── Rescue Chambers ───────────────────────────────────────────────
    // panelFilter === 'chamber'
    const all = Object.values(chambers)
    const totalCount = all.length
    const stats = aggregateStats(all.map(c => normalizeChamberStatus(c.status)))
    const filtered = all.filter(c => matchesLevel(c.level))

    const entities: EntityCardProps[] = filtered.map(c => ({
      kind: 'chamber',
      id: c.id,
      primary: c.name,
      secondary: c.location,
      meta: `${c.currentOccupancy}/${c.capacity} · ${c.capacity > 0
        ? Math.round((c.currentOccupancy / c.capacity) * 100)
        : 0}% full`,
      statusKey: normalizeChamberStatus(c.status),
      statusLabel: c.status.replace(/_/g, ' '),
      onClick: () => {
        sceneRef.current?.camera.flyTo(c.position, 120, 1.2, {x: 0, y: 0, z: 1})
      },
      // Chambers are read-only here — no onEdit (TagBoard owns chamber CRUD).
    }))
    entities.sort(byPrimary)
    return {
      entities,
      totalCount,
      visibleCount: entities.length,
      headerLabel,
      supportsStatusFilter: false,
      addsTo: null,
      stats,
    }
  }, [
    panelFilter, levelFilter, statusFilter,
    assets, locations, accessPoints, chambers, selectedId,
    dispatch, sceneRef, onEdit,
  ])
}
